import React, { useState } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';

// COMPONENTS
import {
  Box,
  Link,
  Typography,
  CardArt,
  Icon,
  Element,
  FontType,
  Color,
  Size,
  IconBulletedList,
  VAlign
} from '@americanexpress/aqx-components';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import { StrikethroughOffer } from 'src/universal/components/Strikethrough';
import Ribbon, {
  POSITIONS
} from 'page_modules/business-vac/components/CardTileDesktop/Components/Ribbon.js';
import LazyLoad from 'src/universal/components/DTM/LazyScrollMetricTracker';
import YourCardBadge from 'src/universal/components/YourCardBadge';
import AmazonDisclaimer from 'page_modules/business-vac/components/AmazonDisclaimer';
import ApplyNowCta from 'page_modules/business-vac/components/CardTileDesktop/Components/ApplyNowCta';
import UpgradeCta from 'page_modules/business-vac/components/CardTileDesktop/Components/UpgradeCta';
import AdditionalUpgradeText from 'page_modules/business-vac/components/CardTileDesktop/Components/AdditionalUpgradeText';
import LearnMoreCta from 'page_modules/business-vac/components/CardTileDesktop/Components/LearnMoreCta';
import CheckboxCompareButton from 'src/universal/components/CheckboxCompareButton';
import AddEmployeeCta from 'page_modules/business-vac/components/CardTileDesktop/Components/AddAnEmployeeCta';

// SELECTORS
import {
  getAnnualFeeLabel,
  makeGetAnnualFeeDisclaimer,
  getLabels,
  getDtm,
  makeGetPznOffer,
  makeGetCardEep,
  makeGetFeaturedBenefits
} from 'page_modules/business-vac/components/CardTileMobile/redux';

import {
  makeGetQuickViewItems,
  makeGetRate,
  makeGetCardTitle,
  makeGetExtendedTilesData
} from 'page_modules/business-vac/components/CardTileDesktop/redux';

import {
  makeGetIsTier2Offer,
  makeGetIsTier1Offer,
  makeGetDefaultTier1OfferBadgeText,
  makeGetOfferBadgeText
} from 'page_modules/business-vac/components/CardTileDesktop/redux.js';

// UTILS
import {
  mapUrlWithDtmLinkQuery,
  sendDTMClickEvent
} from 'service_modules/models/dtm-tagging';
import { getUrlWithOriginPrepended } from 'core_modules/utils/url.utils';
import { processTncUrl } from 'service_modules/models/shared/cross-model-helper';
import { cmComponentPznExt } from 'src/universal/utils/orchestraCmPznExtEvent';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';

// STYLES
import styles from 'page_modules/business-vac/__styles__/listStyles.scss?modules';

const DualOfferSeparator = () => (
  <div className={styles.separator}>
    <div className={styles.separatorLine} />
    <Icon
      className={styles.separatorIcon}
      directory="dls"
      imageName="dls-icon-plus-filled"
    />
    <div className={styles.separatorLine} />
  </div>
);

const ListTile = ({
  isInCurrentFilter,
  card,
  cardArtUrl,
  annualFeeLabel,
  feeDisclaimer,
  labels,
  dtm,
  cardEep,
  pznOffer,
  isTier1Offer,
  isTier2Offer,
  offerBadgeText,
  defaultTier1OfferBadgeText,
  quickViewItems,
  rate,
  tileData,
  cardTitle,
  featuredBenefits
}) => {
  const [open, setOpen] = useState(false);
  const { cardHeadline, annualFee, learnMoreButton, tncLinks, shortName } = card;
  const annualFeeText = (annualFee ?? '').replace(' Annual Fee', '');
  const annualFeeLabelText = annualFeeLabel?.text;
  const cardURL = learnMoreButton?.url ?? '';
  const pdpUrl = mapUrlWithDtmLinkQuery(
    getUrlWithOriginPrepended(cardURL),
    listViewClickEvent
  );
  const { offerTerms, ratesFees } = tncLinks;
  const cardHasOffer = !!pznOffer?.header;
  const {
    header,
    description,
    content,
    treatmentId,
    componentId,
    iaCode
  } = pznOffer;
  const cardOffer = cardHasOffer
    ? {
        header,
        description,
        dualOfferHeader: content?.[0]?.header,
        dualOfferDescription: content?.[0]?.description
      }
    : {
        header: card.cardFeatures.header,
        description: card.cardFeatures.descriptionList[0]
      };
  const { listOfferTerms, listFees, listClick } = dtm;
  const listViewClickEvent = {
    ...listClick,
    eventAsset: `${listClick?.eventAsset}${card.dtmProductName}`,
    tag: `${listClick?.tag}${card.dtmProductName}`
  };
  const badgeText =
    !isTier2Offer && isTier1Offer ? defaultTier1OfferBadgeText : offerBadgeText;

  const parsedDtmEvents = parseArrayToObject(tileData?.mobileDtm || [], 'key');

  const showHideDtmClickEvent = () => {
    const dtmObj = open
      ? parsedDtmEvents?.hideOffersClickMobileB
      : parsedDtmEvents?.showOffersClickMobileB;
    const dtm = {
      ...(dtmObj || {}),
      eventAsset: replaceStringWithVars({
        varsObject: {
          cardName: shortName
        },
        string: dtmObj?.eventAsset || ''
      })
    };
    sendDTMClickEvent(dtm);
  };

  const drawerButtonClick = () => {
    setOpen(!open);
    showHideDtmClickEvent();
  };

  const getButtonTitle = () => {
    if (open) {
      if (!offerBadgeText) {
        return tileData?.mobileButtons?.hideBenefitsButton;
      } else {
        return tileData?.mobileButtons?.hideOfferButton;
      }
    } else {
      if (!offerBadgeText) {
        return tileData?.mobileButtons?.showBenefitsButton;
      } else {
        return tileData?.mobileButtons?.showOfferButton;
      }
    }
  };

  return (
    <React.Fragment>
      <div style={{ display: isInCurrentFilter ? 'block' : 'none' }}>
        <Box
          $border={true}
          className={'mobileCardTile'}
          theme={{
            marginBottom: 10,
            rounded: Size.XS,
            borderColor: isTier1Offer ? Color.DeepBlue : Color.Gray03
          }}
        >
          <LazyScrollMetricTracker
            offset={-50}
            callBackFunction={() => {
              if ((cardHasOffer && treatmentId, componentId)) {
                cmComponentPznExt({ id: treatmentId, compId: componentId, iaCode });
              }
            }}
          />
          {isTier1Offer && <div className={styles.tier1Badge}>{badgeText}</div>}
          <div>
            <div className={styles.header}>
              <LazyLoad offset={200}>
                <CardArt src={cardArtUrl} alt={card?.cardArtAltText} />
              </LazyLoad>
              <Link
                href={pdpUrl}
                onClick={() => sendDTMClickEvent(listViewClickEvent)}
              >
                <h3>
                  <Typography
                    $size="20px"
                    $lineHeight="28px"
                    className="card-title-wrap"
                    $family={FontType.BentonRegular}
                    theme={{
                      color: Color.BrightBlue
                    }}
                  >
                    {cardHeadline}
                  </Typography>
                  <YourCardBadge shortName={shortName} />
                </h3>
                <Icon
                  directory="dls"
                  imageName="dls-icon-right"
                  $size={Size.MD}
                  theme={{
                    color: Color.BrightBlue
                  }}
                />
              </Link>
            </div>
          </div>

          <Typography
            $size="15px"
            $lineHeight="22px"
            className={styles.benefitsHeader}
            $family={FontType.HelveticaRegular}
            theme={{
              color: Color.DeepBlue,
              marginLeft: 20,
              marginTop: 20
            }}
          >
            <Sanitizer safelySetInnerHTML={tileData?.benefitsHeader ?? ''} />
          </Typography>
          {!open && (
            <div className={clsx(styles.listWrapper, styles.topBenefits)}>
              <IconBulletedList
                data={{
                  bulletPoints: featuredBenefits.map(eachBulletPoint => ({
                    icon: {
                      directory: 'dls',
                      imageName: eachBulletPoint.icon,
                      $size: Size.SM,
                      hiddenToScreenReaders: true
                    },
                    text: eachBulletPoint.text
                  }))
                }}
                $iconAlign={VAlign.Top}
                customization={{ listItem: { paddingLeft: 10 } }}
              />
            </div>
          )}
          {!open && (
            <div className={styles.drawerButton}>
              <button onClick={drawerButtonClick}>
                {getButtonTitle()}
                <Icon
                  directory="dls"
                  imageName={`dls-icon-${open ? 'up' : 'down'}`}
                  $size={Size.SM}
                  theme={{
                    marginLeft: 10,
                    color: Color.BrightBlue
                  }}
                />
              </button>
            </div>
          )}
          <div
            className={styles.drawerBox}
            style={{ display: open ? 'block' : 'none' }}
          >
            <div className={styles.listWrapper}>
              <IconBulletedList
                data={{
                  bulletPoints: quickViewItems.map(eachBulletPoint => ({
                    icon: {
                      directory: 'dls',
                      imageName: eachBulletPoint.icon,
                      $size: Size.SM,
                      hiddenToScreenReaders: true
                    },
                    text: `<span class=${styles.bulletHeader}>${
                      eachBulletPoint.title
                        ? eachBulletPoint.title
                        : eachBulletPoint.header
                    }</span>${eachBulletPoint.text ? eachBulletPoint.text : rate}`
                  }))
                }}
                $iconAlign={VAlign.Top}
                customization={{ listItem: { paddingLeft: 10 } }}
              />
            </div>
            {open && (
              <div className={styles.drawerButton}>
                <button onClick={drawerButtonClick}>
                  {getButtonTitle()}
                  <Icon
                    directory="dls"
                    imageName={`dls-icon-${open ? 'up' : 'down'}`}
                    $size={Size.SM}
                    theme={{
                      marginLeft: 20,
                      color: Color.BrightBlue
                    }}
                  />
                </button>
              </div>
            )}
            <div className="annual-fee">
              <Typography
                $size="15px"
                $lineHeight="22px"
                theme={{
                  color: Color.DeepBlue,
                  marginBottom: 20
                }}
              >
                <Sanitizer safelySetInnerHTML={annualFeeLabelText} />
                <Sanitizer safelySetInnerHTML={annualFeeText} class="card-fee" />
                {feeDisclaimer && (
                  <Sanitizer
                    Component="div"
                    className="fee-disclaimer"
                    safelySetInnerHTML={feeDisclaimer}
                  />
                )}
              </Typography>
            </div>
            {tileData?.offerHeader &&
              offerBadgeText && (
                <Typography
                  $size="15px"
                  $lineHeight="22px"
                  $family={FontType.HelveticaRegular}
                  className={styles.offerHeader}
                  theme={{
                    color: Color.DeepBlue,
                    marginTop: 20
                  }}
                >
                  {tileData?.offerHeader}
                </Typography>
              )}
            <Box
              theme={{
                bgColor: Color.Gray01,
                marginBottom: 20
              }}
              className={clsx(styles.offerBox, styles.g172Offer, {
                [styles.boxBorder]: isTier2Offer && !isTier1Offer,
                [styles.partBorder]: isTier1Offer
              })}
            >
              <Ribbon
                position={POSITIONS.OFFER_BOX_RIBBON}
                shortName={card.shortName}
              />
              <StrikethroughOffer
                header={cardOffer?.header}
                Component="h3"
                className={styles.offerHeader}
              />
              <Typography
                $size="15px"
                $lineHeight="22px"
                $family={FontType.HelveticaRegular}
                theme={{
                  color: Color.Gray06,
                  marginTop: 0
                }}
              >
                <Sanitizer safelySetInnerHTML={cardOffer?.description} />
              </Typography>
              {cardOffer?.dualOfferHeader && (
                <>
                  <DualOfferSeparator />
                  <StrikethroughOffer
                    header={cardOffer?.dualOfferHeader}
                    Component="h3"
                    className={styles.offerHeader}
                  />
                  <Typography
                    $size="15px"
                    $lineHeight="22px"
                    $family={FontType.HelveticaRegular}
                    theme={{
                      color: Color.Gray06
                    }}
                  >
                    <Sanitizer
                      safelySetInnerHTML={cardOffer?.dualOfferDescription}
                    />
                  </Typography>
                </>
              )}
            </Box>
            <div className={styles.ctaContainer}>
              <LearnMoreCta shortName={shortName} />
              <UpgradeCta shortName={shortName} />
              {open && <ApplyNowCta shortName={shortName} />}
              <AdditionalUpgradeText shortName={shortName} />
            </div>
            <div className={styles.compareBox}>
              <CheckboxCompareButton
                cardTitle={cardTitle}
                shortName={shortName}
                mobile
              />
              <AddEmployeeCta shortName={shortName} />
            </div>
          </div>
          <div
            className={styles.tncBorder}
            style={{
              borderTop: '1px solid #ECEDEE'
            }}
          />
          <AmazonDisclaimer shortName={card.shortName} />
          <Element className={styles.bottomBox}>
            {offerTerms && (
              <Link
                theme={{
                  color: Color.BrightBlue,
                  textDecoration: 'underline',
                  fontType: FontType.HelveticaRegular
                }}
                onClick={() => sendDTMClickEvent({ ...listOfferTerms })}
                style={{ fontSize: '13px' }}
                target="_blank"
                href={processTncUrl(
                  {
                    url: offerTerms.url,
                    urlAppend: offerTerms.urlAppend
                  },
                  cardEep
                )}
              >
                <Sanitizer safelySetInnerHTML={labels?.offerTerms?.text ?? ''} />
              </Link>
            )}
            {ratesFees && (
              <Link
                theme={{
                  color: Color.BrightBlue,
                  textDecoration: 'underline',
                  fontType: FontType.HelveticaRegular
                }}
                onClick={() => sendDTMClickEvent({ ...listFees })}
                target="_blank"
                style={{ fontSize: '13px' }}
                href={processTncUrl(
                  {
                    url: ratesFees.url,
                    urlAppend: ratesFees.urlAppend
                  },
                  cardEep
                )}
              >
                <Sanitizer safelySetInnerHTML={labels?.ratesFees?.text ?? ''} />
              </Link>
            )}
          </Element>
        </Box>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = () => {
  const getAnnualFeeDisclaimer = makeGetAnnualFeeDisclaimer();
  const getPznOffer = makeGetPznOffer();
  const getCardEep = makeGetCardEep();
  const getIsTier2Offer = makeGetIsTier2Offer();
  const getIsTier1Offer = makeGetIsTier1Offer();
  const getOfferBadgeText = makeGetOfferBadgeText();
  const getDefaultTier1OfferBadgeText = makeGetDefaultTier1OfferBadgeText();
  const getQuickViewItems = makeGetQuickViewItems();
  const getRate = makeGetRate();
  const getCardTitle = makeGetCardTitle();
  const getFeaturedBenefits = makeGetFeaturedBenefits();
  const getCardTileData = makeGetExtendedTilesData();

  return (state, { shortName }) => ({
    isTier1Offer: getIsTier1Offer(state, shortName),
    isTier2Offer: getIsTier2Offer(state, shortName),
    offerBadgeText: getOfferBadgeText(state, shortName),
    defaultTier1OfferBadgeText: getDefaultTier1OfferBadgeText(state, shortName),
    annualFeeLabel: getAnnualFeeLabel(state),
    feeDisclaimer: getAnnualFeeDisclaimer(state, shortName),
    labels: getLabels(state),
    dtm: getDtm(state),
    pznOffer: getPznOffer(state, shortName),
    cardEep: getCardEep(state, shortName),
    quickViewItems: getQuickViewItems(state, shortName),
    rate: getRate(state, shortName),
    tileData: getCardTileData(state),
    cardTitle: getCardTitle(state, shortName),
    featuredBenefits: getFeaturedBenefits(state, shortName)
  });
};

export default connect(mapStateToProps)(ListTile);
